import React from 'react';

/**
 * Error Boundary: catches JavaScript errors in the child tree and shows a fallback UI
 * instead of a blank screen. Includes a "Reload" action to recover.
 * Must be a class component (React only supports error boundaries in class components).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback } = this.props;
      if (Fallback) {
        return <Fallback error={this.state.error} onReload={this.handleReload} />;
      }
      return (
        <div
          className="min-h-screen flex items-center justify-center p-6"
          style={{
            backgroundColor: 'var(--color-background, #f8f9fa)',
            color: 'var(--color-text, #2c3e50)',
          }}
        >
          <div className="max-w-md w-full text-center">
            <div className="mb-4 text-5xl" role="img" aria-hidden="true">
              ⚠️
            </div>
            <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
            <p className="text-sm opacity-80 mb-6">
              An unexpected error occurred. You can try reloading the page to continue.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-2 rounded-lg font-medium transition-colors min-h-[44px] min-w-[44px] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                backgroundColor: 'var(--color-primary, #3498db)',
                color: '#fff',
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
